import coreIcon from "./imgs/core.svg";
import proIcon from "./imgs/pro.svg";
import type { PlanTier, TrialExpiredPricing } from "src/domains/billing/trial";

// Every currency the plans are sold in. Keep in step with the billing
// catalogue - a currency missing here leaves its trialists with no modal.
type Currency = "gbp" | "usd" | "eur" | "cad";

type Plan = {
  name: {
    id: string;
    defaultMessage: string;
  };
  icon: string;
  description: {
    id: string;
    defaultMessage: string;
  };
  price: {
    monthly: Record<Currency, { cost: number; unit: string }>;
    yearly: Record<Currency, { cost: number; unit: string }>;
  };
  featureTitle: {
    id: string;
    defaultMessage: string;
  };
  features: {
    id: string;
    defaultMessage: string;
  }[];
};

type Plans = {
  core: Plan;
  pro: Plan;
};

/**
 * The tier of a billing v1 plan id, `{tier}-{period}-{currency}` (e.g. `core-year-gbp`). Null for any other plan.
 */
export const toV1PlanTier = (planId: string | null): PlanTier | null => {
  if (!planId || !planId.includes("-")) {
    return null;
  }
  const tier = planId.split("-")[0];
  return tier === "core" || tier === "pro" ? tier : null;
};

/**
 * Billing v1 prices for the trial expired modal, from the table below. Billing v2 reads its catalogue instead.
 *
 * @returns Null when we hold no prices for the plan's currency: the caller then skips the modal rather than showing
 * an empty dialog. An id with no currency segment defaults to gbp.
 */
export const toV1TrialExpiredPricing = (
  planId: string | null,
): TrialExpiredPricing | null => {
  const tier = toV1PlanTier(planId);
  if (!tier) {
    return null;
  }
  const currency = (planId?.split("-")[2] ?? "gbp") as Currency;
  const monthly = plans[tier].price.monthly[currency];
  const yearly = plans[tier].price.yearly[currency];
  if (!monthly || !yearly) {
    return null;
  }
  return {
    monthly: `${monthly.unit}${monthly.cost}`,
    // The modal has always shown the monthly unit beside either price.
    yearly: `${monthly.unit}${yearly.cost}`,
  };
};

export const plans: Plans = {
  core: {
    name: {
      id: "trial_expire_modal.planName.core",
      defaultMessage: "Core",
    },
    icon: coreIcon,
    description: {
      id: "trial_expire_modal.planDescription.core",
      defaultMessage:
        "Showcase your brand, tell stories, and engage customers and employees",
    },
    price: {
      monthly: {
        gbp: { cost: 18, unit: "£" },
        usd: { cost: 24, unit: "$" },
        eur: { cost: 21, unit: "€" },
        cad: { cost: 32, unit: "$" },
      },
      yearly: {
        gbp: { cost: 16, unit: "£" },
        usd: { cost: 20, unit: "$" },
        eur: { cost: 18, unit: "€" },
        cad: { cost: 27, unit: "$" },
      },
    },
    featureTitle: {
      id: "trial_expire_modal.featureTitle.core",
      defaultMessage: "Featuring",
    },
    features: [
      {
        id: "trial_expire_modal.features.core.feature1",
        defaultMessage: "70+ apps & integrations",
      },
      {
        id: "trial_expire_modal.features.core.feature2",
        defaultMessage: "Unlimited users",
      },
      {
        id: "trial_expire_modal.features.core.feature3",
        defaultMessage: "24/5 professional support",
      },
    ],
  },
  pro: {
    name: {
      id: "trial_expire_modal.planName.pro",
      defaultMessage: "Pro",
    },
    icon: proIcon,
    description: {
      id: "trial_expire_modal.planDescription.pro",
      defaultMessage:
        "Expand your screen network and securely amplify messages to your audience",
    },
    price: {
      monthly: {
        gbp: { cost: 24, unit: "£" },
        usd: { cost: 30, unit: "$" },
        eur: { cost: 27, unit: "€" },
        cad: { cost: 47, unit: "$" },
      },
      yearly: {
        gbp: { cost: 24, unit: "£" },
        usd: { cost: 30, unit: "$" },
        eur: { cost: 27, unit: "€" },
        cad: { cost: 40, unit: "$" },
      },
    },
    featureTitle: {
      id: "trial_expire_modal.featureTitle.pro",
      defaultMessage: "Core plan features plus:",
    },
    features: [
      {
        id: "trial_expire_modal.features.pro.feature1",
        defaultMessage: "Microsoft integrations",
      },
      {
        id: "trial_expire_modal.features.pro.feature2",
        defaultMessage: "Secure dashboards",
      },
      {
        id: "trial_expire_modal.features.pro.feature3",
        defaultMessage: "Remote device management",
      },
    ],
  },
};
